import { Grid, useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useJobApplication from "../../../hooks/useJobApplication";
import {
  updateBulkApplyState,
  updatePostApplyDialog,
  updateShowSimilarJobsStatus,
} from "../../../redux/actions/dialog_action";
import {
  bulkApplyJobs,
  clearBulkAppliedJobs,
  clearTotalBulkAppliedJobs,
  getPostApplyRecommendedJobs,
  triggerJobReload,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import LanguageProficiencyDialog from "../LanguageProficiencyDialog/LanguageProficiencyDialog";
import AppliedJobList from "./AppliedJobList/AppliedJobList";
import BulkApplyList from "./BulkApplyList/BulkApplyList";
import MobileAppAd from "./MobileAppAd/MobileAppAd";
import SecondaryBulkApply from "./SecondaryBulkApply/SecondaryBulkApply";
import {
  AppliedButtonStyled,
  AppliedJobContainer,
  AppliedJobWrapper,
  BoxContainer,
  ButtonStyled,
  CheckIcon,
  CloseButton,
  CloseIcon,
  ContentText,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  HeaderStyled,
  LinkStyled,
  TextStyled,
  WarnIcon,
} from "./styles";

export default function PostApplyDialog(props) {
  const {
    page,
    origin,
    handleTabChange,
    setDialogOpen,
    jobListRef,
    selectedTab,
    specialisationTitle = null,
    onInternshipRefresh = null,
  } = props;

  const [openLangDialog, setOpenLangDialog] = useState(false);
  const [afterLangClose, setAfterLangClose] = useState(null);
  const [pendingLangOpen, setPendingLangOpen] = useState(false);
  const user = useSelector((state) => state.user?.user);

  const languageDialogDontShowSessionKey = useMemo(() => {
    const userId = user?.id ?? "visitor";
    return `languageProficiencyDialogDontShowSession_${userId}`;
  }, [user?.id]);

  const hasLanguageProficiency = () => {
    if (typeof window === "undefined") return false;

    if (localStorage.getItem("languageProficiencyCompleted") === "true") {
      return true;
    }

    try {
      const raw = localStorage.getItem("USER_PROFILE");
      if (!raw) return false;
      const profile = JSON.parse(raw);
      const langs = profile?.languages ?? [];

      if (Array.isArray(langs)) return langs.length > 0;
      if (langs?.nodes && Array.isArray(langs.nodes))
        return langs.nodes.length > 0;

      return false;
    } catch {
      return false;
    }
  };

  const openLanguageThen = (fn) => {
    const isSignedIn = !!user?.id;
    const dontShowThisSession =
      typeof window !== "undefined" &&
      sessionStorage.getItem(languageDialogDontShowSessionKey) === "true";

    if (!isSignedIn || dontShowThisSession || hasLanguageProficiency()) {
      fn();
      return;
    }

    setAfterLangClose(() => fn);
    setPendingLangOpen(true);
    handleCloseDialog();
    setOpenLangDialog(true);
  };

  const closeLanguageAndContinue = () => {
    setOpenLangDialog(false);
    setAfterLangClose((queued) => {
      if (queued) queued();
      return null;
    });
  };

  const isMobile = useMediaQuery("(max-width: 640px)");

  const router = useRouter();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const { t } = useTranslation();

  let originUrl = "";

  if (typeof window !== "undefined") {
    originUrl = localStorage?.getItem("apply_origin") ?? "";
  }

  const [showList, setShowList] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const userResume = useSelector((state) => state?.user?.user?.state);

  const {
    status: showDialog,
    scraped: isScraped,
    jobGlobalId,
    jobId,
    internship,
  } = useSelector((state) => state.dialog.showPostApplyDialog);

  const bulkApplyState = useSelector((state) => state?.dialog?.bulkApplyState);
  const applyingJobs = useSelector((state) => state?.jobs?.applyingJobs);
  const bulkAppliedJobs = useSelector((state) => state?.jobs?.bulkAppliedJobs);
  const bulkAppliedJobsTotal = useSelector(
    (state) => state?.jobs?.bulkAppliedJobsTotal
  );
  const jobReload = useSelector((state) => state?.jobs?.jobReload);
  const recommendedJobs =
    useSelector((state) => state?.jobs?.recommendedJobs) || [];
  const fetchingRecommendedJobs = useSelector(
    (state) => state?.jobs.fetchingRecommendedJobs
  );
  const similarJobsTriggered = useSelector(
    (state) => state.dialog?.showSimilarJobs
  );

  // Memoize the selected jobs to only rerender on currentPage or recommendedJobs change
  const filteredJobs = useMemo(() => {
    if (!recommendedJobs) return [];
    const startIndex = currentPage * 6;
    return recommendedJobs?.slice(startIndex, startIndex + 6);
  }, [recommendedJobs, currentPage]);

  const jobOrigin = "job_list";
  const jobDiscovery = "search";
  const fullUrl = router?.asPath;

  const {
    checkLoggedIn,
    checkNationalityPhoneNumberLocation,
    checkRightToWorkAndRelocate,
    checkResumeRejected,
    checkScrapedJob,
  } = useJobApplication(jobOrigin, jobDiscovery);

  const selectedJobsLength = selectedJobs?.length ?? 0;
  const bulkApplyJobsStatus =
    bulkAppliedJobs?.length > 0 && bulkApplyState === "applied";

  const isLoadingJobs = fetchingRecommendedJobs || isLoading;

  const DialogHeaderIcon = useMemo(() => {
    if (!showDialog) return "";
    if (
      page === "internship-bulk" &&
      bulkApplyState !== "secondary-bulk" &&
      !bulkApplyJobsStatus
    )
      return <p style={{ margin: 0 }}>📝</p>;
    if ((isScraped && !bulkApplyJobsStatus) || userResume === "in_review")
      return <WarnIcon />;
    if (userResume === "approved") return <CheckIcon />;
    return <WarnIcon />;
  }, [isScraped, userResume, showDialog, bulkApplyJobsStatus]);

  const DialogHeaderTitle = useMemo(() => {
    if (!showDialog) return "";
    if (isScraped && !bulkApplyJobsStatus)
      return t("jobs.post_apply_dialog.header_scraped");
    if (
      page === "internship-bulk" &&
      bulkApplyState !== "secondary-bulk" &&
      !bulkApplyJobsStatus
    )
      return t("jobs.post_apply_dialog.header_select_jobs");
    if (userResume === "approved")
      return t("jobs.post_apply_dialog.header_application_successful");
    return t("jobs.post_apply_dialog.header_application_in_progress");
  }, [isScraped, userResume, showDialog, bulkApplyJobsStatus, t]);

  const DialogHeaderSubtitle = useMemo(() => {
    if (isScraped) {
      return t("jobs.post_apply_dialog.subtitle_scraped");
    }

    if (page === "internship-bulk" && !bulkApplyJobsStatus) {
      return null;
    }

    const redirectLink = (
      <LinkStyled
        onClick={handleViewJobApplicationHistory}
        textDecoration="underline"
      >
        {t("jobs.post_apply_dialog.job_application_status")}
      </LinkStyled>
    );

    const subtitleCopy =
      userResume === "approved"
        ? t("jobs.post_apply_dialog.subtitle_approved")
        : t("jobs.post_apply_dialog.subtitle_pending");

    return (
      <>
        {subtitleCopy}{" "}
        {redirectLink}
      </>
    );
  }, [isScraped, userResume, page, bulkApplyJobsStatus, t]);

  const ApplyButtonText = t(
    selectedJobsLength === 6
      ? "jobs.post_apply_dialog.apply_button_all"
      : "jobs.post_apply_dialog.apply_button_selected",
    { count: selectedJobsLength }
  );

  const disableApplyButton = selectedJobsLength === 0 || applyingJobs;

  function handleCloseDialog() {
    setSelectedJobs([]);
    dispatch(clearTotalBulkAppliedJobs());
    dispatch(clearBulkAppliedJobs());
    dispatch(
      updatePostApplyDialog({
        status: false,
        scraped: false,
        jobGlobalId: null,
        internship: false,
      })
    );
    if (similarJobsTriggered) {
      sendTrackingEvent({
        event: "CE_view_similar_jobs_back_click",
        origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
        job_discovery: "view_similar_jobs",
        step: "view_similar_jobs_click",
      });
    }
    localStorage?.removeItem("scraped_job_id");
  }

  function handleCloseAndRedirect() {
    openLanguageThen(() => {
      const dontShow = localStorage.getItem(
        `currentSalaryDialogDontShow_${user?.id}`
      );
      const shownSession = localStorage.getItem(
        `currentSalaryDialogShownSession_${user?.id}`
      );

      const origin =
        fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page";

      if (bulkApplyJobsStatus || page === "for-you") {
        dispatch(triggerJobReload(!jobReload));
      }
      setDialogOpen(true);
      handleCloseDialog();
      dispatch(updateShowSimilarJobsStatus(false));
      dispatch(updateBulkApplyState("post-apply"));
      if (page === "internship-bulk") {
        setCurrentPage(0);
        setDialogOpen(false);
      }

      if (user?.id && !user?.currentSalary && !dontShow && !shownSession) {
        localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
      }

      if (router?.pathname?.includes("/apply")) {
        if (originUrl) {
          router?.push(originUrl);
          localStorage?.removeItem("apply_origin");
          localStorage?.removeItem("bulkApplyFromSimilarJob");
        } else {
          router?.push("/jobs");
        }
      }
    });
  }

  function handleViewAllJobs() {
    const dontShow = localStorage.getItem(
      `currentSalaryDialogDontShow_${user?.id}`
    );
    const shownSession = localStorage.getItem(
      `currentSalaryDialogShownSession_${user?.id}`
    );
    if (page === "job-list") {
      return handleCloseAndRedirect();
    }

    if (user?.id && !user?.currentSalary && !dontShow && !shownSession) {
      localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
    }
    handleCloseDialog();
    if (page === "for-you") {
      handleTabChange("job-list");
    } else {
      router.push("/jobs");
    }

    return router.push("/jobs");
  }

  async function handleApplyBulkJob(jobs) {
    const jobIds = jobs.map((job) => job.id);

    let applySourceNames =
      // Case 1: Profile - Application Applied
      fullUrl.includes("job-application-history") && origin === "Applied"
        ? similarJobsTriggered
          ? "profile_application_applied_bulk_apply_view_similar_jobs"
          : "profile_application_applied_bulk_apply_post_apply_rec"
        : // Case 2: Job Listing Page (JLP)
        selectedTab === "job-list"
        ? "jlp_bulk_apply_post_apply_rec"
        : // Case 3: For You Page (FYP)
        selectedTab === "for-you"
        ? "fyp_bulk_apply_post_apply_rec"
        : // Case 4: Profile - Application Recommended
        fullUrl.includes("job-application-history")
        ? "profile_application_recommend_bulk_apply_post_apply_rec"
        : // Case 5: Profile - Saved Jobs and Companies
        fullUrl.includes("saved-jobs-&-companies")
        ? selectedTab === "saved-jobs"
          ? "profile_saved_jobs_bulk_apply_post_apply_rec"
          : "profile_saved_recommend_bulk_apply_post_apply_rec"
        : // Case 6: Bulk Internship
        selectedTab === "internship-bulk"
        ? "ibp_bulk_apply"
        : // Case 7: Default - from Job Details Page (JDP)
        similarJobsTriggered
        ? "jdp_bulk_apply_view_similar_jobs"
        : "jdp_bulk_apply_post_apply_rec";

    const params = {
      jobIds: jobIds,
      applySource: applySourceNames,
    };

    // If multiple jobs are selected
    if (jobs.length > 1) {
      // NOT LOGGED IN
      if (checkLoggedIn(jobs, true)) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        // CE to track visitor click on continue button
        sendTrackingEvent({
          event: "CE_view_similar_jobs_continue_click",
          origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
          job_discovery: "view_similar_jobs",
          is_visitor: true,
        });
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
      if (
        page !== "internship-bulk" && // CONDITION - NO NATIONALITY OR MOBILE NUMBER OR CURRENT LOCATION
        (checkNationalityPhoneNumberLocation(jobs, true) ||
          // CONDITION - NO RESUME / RESUME REJECTED
          checkResumeRejected(jobs, true))
      ) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
    } else {
      if (checkLoggedIn(jobs[0])) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        sendTrackingEvent({
          event: "CE_view_similar_jobs_continue_click",
          origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
          job_discovery: "view_similar_jobs",
          is_visitor: true,
        });
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }

      if (
        page !== "internship-bulk" &&
        (checkNationalityPhoneNumberLocation(jobs[0]) ||
          // CONDITION - RIGHT TO WORK OR RELOCATION PREFERENCE
          checkRightToWorkAndRelocate(jobs[0]) ||
          checkResumeRejected(jobs[0]) ||
          // CONDITION - SCRAPED JOB
          checkScrapedJob(jobs[0]))
      ) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
    }

    setDialogOpen(true);
    const res = await dispatch(bulkApplyJobs(params));

    if (res.success) {
      // auto transition to applied if single page specialization, only for internship-bulk
      if (page === "internship-bulk") {
        const totalPages = Math.ceil(recommendedJobs.length / 6);
        const isLastPage =
          filteredJobs.length > 0 &&
          filteredJobs.at(-1)?.id === recommendedJobs.at(-1)?.id;

        // PostHog tracking (Apply Jobs button) [JSW-3026]
        posthog.capture("job_bulk_apply", {
          from_job: jobGlobalId,
          job_discovery: "internship_bank_page",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        sendTrackingEvent({
          event: "CE_job_bulk_apply",
          from_job: jobGlobalId,
          job_discovery: "internship_bank_page",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        if (totalPages <= 1) {
          dispatch(updateBulkApplyState("applied"));
          return;
        }

        if (isLastPage) {
          dispatch(updateBulkApplyState("applied"));
          return;
        }
      }

      // Skip posthog.capture for internship-bulk as it's already called above
      if (page !== "internship-bulk") {
        posthog.capture(
          //posthog tracking (Apply Jobs button) [JSW-3026]
          "job_bulk_apply",
          {
            from_job: jobGlobalId,
            job_discovery:
              selectedTab == "internship-bulk"
                ? "internship_bank_page"
                : "post_apply_rec",
            num_jobs_apply: jobs.length,
            job_ids: jobIds,
          }
        );
      }

      if (bulkApplyState === "secondary-bulk") {
        sendTrackingEvent({
          event: "CE_job_bulk_apply",
          from_job: jobGlobalId,
          job_discovery:
            selectedTab == "internship-bulk"
              ? "internship_bank_page"
              : "post_apply_rec",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        // Dispatch applied state only if its on last job
        if (filteredJobs.at(-1)?.id === recommendedJobs.at(-1)?.id) {
          dispatch(updateBulkApplyState("applied"));
          return;
        }

        setIsLoading(true);
        setCurrentPage(currentPage + 1);

        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      } else {
        setIsLoading(true);

        const params = {
          globalId: jobGlobalId,
          first: 6,
        };

        //CE tracking (Bulk Apply Jobs button - registered users only) [JSW-3329]
        sendTrackingEvent({
          event: "CE_job_bulk_apply",
          from_job: jobGlobalId,
          job_discovery:
            selectedTab == "internship-bulk"
              ? "internship_bank_page"
              : "view_similar_jobs",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        dispatch(updateShowSimilarJobsStatus(false));
        // dispatch(getPostApplyRecommendedJobs(params));
        setCurrentPage(currentPage + 1);
        setTimeout(() => {
          setIsLoading(false);
        }, 300);

        dispatch(updateBulkApplyState("secondary-bulk"));
      }
    }

    dispatch(updateSelectedJob({}));
  }

  function handleViewJobApplicationHistory() {
    window.open("/profile#job-application-history/?pending");
  }

  function handleReviewJobsApply() {
    // Display Current Salary Dialog
    const dontShow = localStorage.getItem(
      `currentSalaryDialogDontShow_${user?.id}`
    );
    const shownSession = localStorage.getItem(
      `currentSalaryDialogShownSession_${user?.id}`
    );

    if (user?.id && !user?.currentSalary && !dontShow && !shownSession) {
      localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
    }

    handleCloseDialog();
    if (!router.pathname.includes("/apply")) {
      router.push("/profile#job-application-history/?pending");
    } else {
      router.push("/profile#job-application-history/?pending");
    }
  }

  useEffect(() => {
    if (jobId || jobGlobalId) {
      const params = {
        jobId: jobId || null,
        first: 60,
        globalId: jobGlobalId || null,
        internship: internship,
      };

      setCurrentPage(0);
      dispatch(getPostApplyRecommendedJobs(params));
    }
  }, [jobGlobalId, jobId, internship]);

  return (
    <>
      <DialogContainer
        $bulkApplyState={bulkApplyState}
        open={showDialog}
        onClose={handleCloseAndRedirect}
        TransitionProps={{
          onExited: () => {
            if (pendingLangOpen) {
              setPendingLangOpen(false);
              setOpenLangDialog(true);
            }
          },
        }}
      >
        {!similarJobsTriggered && (
          <DialogHeader>
            <BoxContainer
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <BoxContainer gap={"0.5rem"} alignItems={"center"}>
                {DialogHeaderIcon}
                <TextStyled
                  fontSize={isMobile ? "0.875rem" : "1.25rem"}
                  fontWeight={"700"}
                  lineHeight={"24px"}
                >
                  {DialogHeaderTitle}
                </TextStyled>
              </BoxContainer>
              <CloseButton onClick={handleCloseAndRedirect}>
                <CloseIcon />
              </CloseButton>
            </BoxContainer>
            <ContentText>{DialogHeaderSubtitle} </ContentText>
          </DialogHeader>
        )}

        <DialogContent $similarJobsTriggered={similarJobsTriggered}>
          {bulkApplyState === "post-apply" ? (
            <BulkApplyList
              dialog={true}
              secondaryBulk={bulkApplyState === "secondary-bulk"}
              selectedJobs={selectedJobs}
              setSelectedJobs={setSelectedJobs}
              fetchingJobs={isLoadingJobs}
              recommendedJobs={recommendedJobs}
              jobs={filteredJobs}
              jobListRef={jobListRef}
              currentTab={selectedTab}
              origin={origin}
              currentPage={currentPage}
              specialisationTitle={specialisationTitle}
            />
          ) : bulkApplyState === "secondary-bulk" ? (
            <SecondaryBulkApply
              dialog={true}
              showList={showList}
              setShowList={setShowList}
              selectedJobs={selectedJobs}
              setSelectedJobs={setSelectedJobs}
              fetchingJobs={isLoadingJobs}
              recommendedJobs={recommendedJobs}
              filteredJobs={filteredJobs}
              jobListRef={jobListRef}
              currentTab={selectedTab}
              origin={origin}
            />
          ) : bulkApplyJobsStatus ? (
            <AppliedJobWrapper>
              <AppliedJobContainer>
                <HeaderStyled>
                  {t("jobs.post_apply_dialog.applied_jobs_header")}
                </HeaderStyled>
                <AppliedJobList jobs={bulkAppliedJobsTotal} />
              </AppliedJobContainer>
              <MobileAppAd />
            </AppliedJobWrapper>
          ) : null}
        </DialogContent>

        {origin == "Applied" ? (
          <Grid>
            {bulkApplyJobsStatus ? (
              <DialogFooter status={bulkApplyJobsStatus}>
                <AppliedButtonStyled onClick={handleReviewJobsApply}>
                  {t("jobs.post_apply_dialog.review_application")}
                </AppliedButtonStyled>
                <AppliedButtonStyled
                  onClick={handleCloseAndRedirect}
                  variant={"filled"}
                  black
                >
                  {t("jobs.post_apply_dialog.done")}
                </AppliedButtonStyled>
              </DialogFooter>
            ) : (
              <DialogFooter origin={origin} status={bulkApplyJobsStatus}>
                {page === "internship-bulk" ? null : (
                  <AppliedButtonStyled
                    variant={"outlined"}
                    onClick={handleViewAllJobs}
                  >
                    {t("jobs.post_apply_dialog.view_all_jobs")}
                  </AppliedButtonStyled>
                )}
                {filteredJobs.length > 0 && (
                  <AppliedButtonStyled
                    onClick={() => handleApplyBulkJob(selectedJobs)}
                    variant={"filled"}
                    disabled={disableApplyButton || isLoadingJobs}
                  >
                    {ApplyButtonText}
                  </AppliedButtonStyled>
                )}
              </DialogFooter>
            )}
          </Grid>
        ) : (
          <Grid>
            <DialogFooter>
              {bulkApplyJobsStatus ? (
                <>
                  <ButtonStyled onClick={handleReviewJobsApply}>
                    {t("jobs.post_apply_dialog.review_application")}
                  </ButtonStyled>
                  <ButtonStyled
                    onClick={handleCloseAndRedirect}
                    variant={"filled"}
                  >
                    {t("jobs.post_apply_dialog.done")}
                  </ButtonStyled>
                </>
              ) : (
                <>
                  {similarJobsTriggered ? (
                    <ButtonStyled
                      onClick={handleCloseDialog}
                      variant={"outlined"}
                    >
                      {t("jobs.post_apply_dialog.back")}
                    </ButtonStyled>
                  ) : page === "internship-bulk" ? null : bulkApplyState ===
                      "post-apply" || bulkApplyState === "secondary-bulk" ? (
                    <ButtonStyled
                      onClick={handleViewAllJobs}
                      variant={"outlined"}
                    >
                      {t("jobs.post_apply_dialog.view_all_jobs")}
                    </ButtonStyled>
                  ) : null}

                  {filteredJobs.length > 0 && (
                    <ButtonStyled
                      onClick={() => handleApplyBulkJob(selectedJobs)}
                      variant={"filled"}
                      color={"primary"}
                      disabled={disableApplyButton || isLoadingJobs}
                    >
                      {ApplyButtonText}
                    </ButtonStyled>
                  )}
                </>
              )}
            </DialogFooter>
          </Grid>
        )}
      </DialogContainer>
      <LanguageProficiencyDialog
        open={openLangDialog}
        onClose={closeLanguageAndContinue}
        onDontShowAgain={() => {
          if (typeof window === "undefined") return;
          sessionStorage.setItem(languageDialogDontShowSessionKey, "true");
        }}
        onDecision={() => closeLanguageAndContinue()}
      />
    </>
  );
}
